import { useCallback, useRef } from 'react';

import { Descendant } from 'slate';

import { CustomEditor, CustomElement } from '../types';
import { restoreEditorFromSnapshot, serializeQueryElements } from '../utils';

const MAX_HISTORY_SIZE = 50;

/**
 * Snapshot-based undo/redo for the query builder.
 *
 * Stores deep-cloned snapshots of the editor state when the filter is complete
 * or empty. Undo/redo restores a previous snapshot by directly replacing
 * editor.children.
 */
export const useEditorHistory = (editor: CustomEditor) => {
  const undoStackRef = useRef<Descendant[][]>([]);
  const redoStackRef = useRef<Descendant[][]>([]);
  const currentSnapshotRef = useRef<Descendant[] | null>(
    editor.children.length > 0 ? structuredClone(editor.children) : null,
  );
  const lastSerializedRef = useRef(
    serializeQueryElements(editor.children as CustomElement[]) ?? '',
  );
  const isRestoringRef = useRef(false);

  const restoreSnapshot = useCallback(
    (snapshot: Descendant[]): CustomElement[] => {
      const restored = restoreEditorFromSnapshot(editor, snapshot);
      currentSnapshotRef.current = structuredClone(restored);
      lastSerializedRef.current = serializeQueryElements(restored as CustomElement[]) ?? '';

      return restored as CustomElement[];
    },
    [editor],
  );

  const saveSnapshot = useCallback(() => {
    if (isRestoringRef.current) {
      return;
    }

    const serialized = serializeQueryElements(editor.children as CustomElement[]) ?? '';
    if (serialized === lastSerializedRef.current) {
      return;
    }

    if (currentSnapshotRef.current) {
      undoStackRef.current.push(currentSnapshotRef.current);
      if (undoStackRef.current.length > MAX_HISTORY_SIZE) {
        undoStackRef.current.shift();
      }
    }

    currentSnapshotRef.current = structuredClone(editor.children);
    lastSerializedRef.current = serialized;
    redoStackRef.current = [];
  }, [editor]);

  const undo = useCallback((): CustomElement[] | undefined => {
    if (undoStackRef.current.length === 0) {
      return undefined;
    }

    isRestoringRef.current = true;

    if (currentSnapshotRef.current) {
      redoStackRef.current.push(currentSnapshotRef.current);
    }

    const result = restoreSnapshot(undoStackRef.current.pop()!);

    isRestoringRef.current = false;

    return result;
  }, [restoreSnapshot]);

  const redo = useCallback((): CustomElement[] | undefined => {
    if (redoStackRef.current.length === 0) {
      return undefined;
    }

    isRestoringRef.current = true;

    if (currentSnapshotRef.current) {
      undoStackRef.current.push(currentSnapshotRef.current);
    }

    const pop = redoStackRef.current.pop();
    const result = restoreSnapshot(pop!);

    isRestoringRef.current = false;

    return result;
  }, [restoreSnapshot]);

  const resetHistory = useCallback(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    lastSerializedRef.current = serializeQueryElements(editor.children as CustomElement[]) ?? '';
    currentSnapshotRef.current =
      editor.children.length > 0 ? structuredClone(editor.children) : null;
  }, [editor]);

  return { redo, resetHistory, saveSnapshot, undo };
};
